import { createContext, useContext, useState, useCallback, type FC, type ReactNode } from 'react';

export type NotificationType = 'error' | 'warning' | 'success' | 'info';

export interface Notification {
  id: string;
  type: NotificationType;
  message: string;
  duration?: number;
}

interface ErrorContextType {
  notifications: Notification[];
  showNotification: (
    type: NotificationType,
    message: string,
    duration?: number
  ) => void;
  removeNotification: (id: string) => void;
}

const ErrorContext = createContext<ErrorContextType | undefined>(undefined);

const DEFAULT_DURATION = 5000;

const generateNotificationId = (): string => 
  Math.random().toString(36).substring(7);

export const ErrorProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const removeNotification = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const showNotification = useCallback(
    (type: NotificationType, message: string, duration: number = DEFAULT_DURATION) => {
      const id = generateNotificationId();
      const notification: Notification = { id, type, message, duration };

      setNotifications((prev) => [...prev, notification]);

      if (duration > 0) {
        setTimeout(() => removeNotification(id), duration);
      }
    },
    [removeNotification]
  );

  return (
    <ErrorContext.Provider value={{ notifications, showNotification, removeNotification }}>
      {children}
      <NotificationContainer notifications={notifications} onRemove={removeNotification} />
    </ErrorContext.Provider>
  );
};

export const useError = (): ErrorContextType => {
  const context = useContext(ErrorContext);
  if (!context) {
    throw new Error('useError must be used within ErrorProvider');
  }
  return context;
};

interface NotificationContainerProps {
  notifications: Notification[];
  onRemove: (id: string) => void;
}

const NotificationContainer: FC<NotificationContainerProps> = ({
  notifications,
  onRemove,
}) => {
  if (notifications.length === 0) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 max-w-md">
      {notifications.map((notification) => (
        <NotificationItem
          key={notification.id}
          notification={notification}
          onRemove={onRemove}
        />
      ))}
    </div>
  );
};

interface NotificationItemProps {
  notification: Notification;
  onRemove: (id: string) => void;
}

const NOTIFICATION_STYLES: Record<NotificationType, string> = {
  error: 'bg-red-500 text-white',
  warning: 'bg-yellow-500 text-white',
  success: 'bg-green-500 text-white',
  info: 'bg-blue-500 text-white',
};

const NOTIFICATION_ICONS: Record<NotificationType, string> = {
  error: '❌',
  warning: '⚠️',
  success: '✅',
  info: 'ℹ️',
};

const NotificationItem: FC<NotificationItemProps> = ({
  notification,
  onRemove,
}) => {
  const styles = NOTIFICATION_STYLES[notification.type];
  const icon = NOTIFICATION_ICONS[notification.type];

  return (
    <div
      className={`${styles} p-4 rounded-lg shadow-lg flex items-start gap-3 transition-transform duration-300 ease-out animate-in slide-in-from-right`}
      role="alert"
    >
      <span className="text-xl">{icon}</span>
      <div className="flex-1">
        <p className="text-sm font-medium">{notification.message}</p>
      </div>
      <button
        onClick={() => onRemove(notification.id)}
        className="text-white hover:text-gray-200 font-bold text-lg leading-none"
        aria-label="Cerrar notificación"
      >
        ×
      </button>
    </div>
  );
};
